import { Building2, ChevronDown, Globe, Link as LinkIcon, Lock } from 'lucide-react';

import { CopyButton } from '@/_shared/components/CopyButton';
import { Button, ButtonGroup } from '@/_shared/components/ui/button';
import { PopoverTrigger } from '@/_shared/components/ui/popover';

export type ShareVisibility = 'private' | 'workspace' | 'public';

interface Props {
  visibility: ShareVisibility;
  shareUrl: string;
  /** Suppress the copy segment even when the visibility would normally render it.
   *  Useful when the caller knows the share URL is currently a dead link (e.g.
   *  the dashboard's public token URL is unavailable because admin disabled
   *  public sharing). The trigger still announces the visibility state via its
   *  icon. */
  noCopy?: boolean;
}

const STATE_ICON: Record<ShareVisibility, typeof Lock> = {
  private: Lock,
  workspace: Building2,
  public: Globe,
};

export function ShareSplitButton({ visibility, shareUrl, noCopy = false }: Props): React.ReactElement {
  const StateIcon = STATE_ICON[visibility];
  const shareable = visibility !== 'private' && !noCopy;

  return (
    <ButtonGroup>
      <PopoverTrigger asChild>
        <Button
          type="button"
          variant="secondary"
          data-testid="share-trigger"
          data-state-icon={visibility}
          Icon={<StateIcon />}
          label="Share"
          suffixIcon={<ChevronDown className="text-muted-foreground" />}
        />
      </PopoverTrigger>
      {shareable && (
        <CopyButton
          text={shareUrl}
          copyLabel="Link"
          copyIcon={<LinkIcon className="text-muted-foreground" />}
          copiedFeedback
          size="md"
          aria-label="Copy link"
          data-testid="copy-link-button"
        />
      )}
    </ButtonGroup>
  );
}
